import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import {
  Package,
  Users,
  DollarSign,
  Filter,
  Download,
  TrendingUp,
  AlertTriangle,
  XCircle,
  Package2,
  PieChart
} from 'lucide-react';

const InventoryReport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Report data state
  const [inventoryData, setInventoryData] = useState({
    totalProducts: 0,
    totalValue: 0,
    lowStockItems: [],
    outOfStockItems: [],
    topMovingProducts: [],
    slowMovingProducts: [],
    supplierBreakdown: [],
    stockAlerts: []
  });

  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [suppliers, setSuppliers] = useState([]);
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');

  // Filter suppliers based on search term
  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(supplierSearchTerm.toLowerCase())
  );


  // Helper function to format currency
  const formatCurrency = (amount) => {
    return `Ghs ${parseFloat(amount || 0).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Fetch inventory report data
  const fetchInventoryReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getInventoryReport(selectedCategory, selectedSupplier);
      if (response.success) {
        setInventoryData(response.data);
        setSuppliers(response.data.suppliers || []);
      } else {
        toast.error('Failed to fetch inventory report data');
      }
    } catch (error) {
      console.error('Error fetching inventory report:', error);
      toast.error('Failed to fetch inventory report data');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedSupplier]);

  // Load data on component mount and category change
  useEffect(() => {
    fetchInventoryReport();
  }, [fetchInventoryReport]);

  // Close supplier dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (supplierDropdownOpen && !event.target.closest('.supplier-dropdown')) {
        setSupplierDropdownOpen(false);
        setSupplierSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [supplierDropdownOpen]);

  // Export report
  const handleExportReport = async (format) => {
    try {
      const response = await apiService.exportInventoryReport(selectedCategory, format);
      if (response.success) {
        // Create download link
        const blob = new Blob([response.data], { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `inventory-report-${selectedCategory}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Inventory report exported successfully as ${format.toUpperCase()}!`, {
          duration: 3000,
          icon: '📦',
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
          },
        });
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export inventory report');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
            {/* Page Header */}
            <div className="mb-6 sm:mb-8 lg:mb-10">
              <div className="flex flex-col gap-4 sm:gap-6">
                {/* Title and Filter Row */}
                <div className="flex flex-col sm:flex-row mt-16 sm:items-center sm:justify-between gap-4">
                  <div className="space-y-2">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                      Inventory Report
                    </h1>
                    {/* <p className="text-gray-600 text-sm sm:text-base lg:text-lg">Comprehensive inventory analytics and stock management insights</p> */}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="all">All Categories</option>
                      <option value="electronics">Electronics</option>
                      <option value="clothing">Clothing</option>
                      <option value="food">Food & Beverages</option>
                      <option value="books">Books</option>
                    </select>
                    {/* Custom Searchable Supplier Dropdown */}
                    <div className="relative supplier-dropdown w-80">
                      <div className="p-[2px] bg-gradient-to-r from-red-500 to-blue-500 rounded-lg">
                        <button
                          type="button"
                          onClick={() => setSupplierDropdownOpen(!supplierDropdownOpen)}
                          className="w-full px-3 py-2 bg-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-left flex items-center justify-between"
                        >
                        <span className="truncate">
                          {selectedSupplier === 'all' ? 'All Suppliers' : selectedSupplier}
                        </span>
                          <svg
                            className={`w-4 h-4 transition-transform ${supplierDropdownOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                      
                      {supplierDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                          {/* Search Input */}
                          <div className="p-3 border-b border-gray-200">
                            <div className="relative">
                              <input
                                type="text"
                                placeholder="Search suppliers..."
                                value={supplierSearchTerm}
                                onChange={(e) => setSupplierSearchTerm(e.target.value)}
                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                              <svg
                                className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {filteredSuppliers.length} of {suppliers.length} suppliers
                            </p>
                          </div>
                          
                          {/* Supplier List */}
                          <div className="max-h-60 overflow-y-auto">
                            <button
                              onClick={() => {
                                setSelectedSupplier('all');
                                setSupplierDropdownOpen(false);
                                setSupplierSearchTerm('');
                              }}
                              className={`w-full px-3 py-2 text-left hover:bg-gray-50 text-sm ${
                                selectedSupplier === 'all' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              All Suppliers
                            </button>
                            {filteredSuppliers.map((supplier) => (
                              <button
                                key={supplier.id}
                                onClick={() => {
                                  setSelectedSupplier(supplier.name);
                                  setSupplierDropdownOpen(false);
                                  setSupplierSearchTerm('');
                                }}
                                className={`w-full px-3 py-2 text-left hover:bg-gray-50 text-sm ${
                                  selectedSupplier === supplier.name ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                              >
                                {supplier.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={fetchInventoryReport}
                      disabled={loading}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      {loading ? 'Loading...' : 'Apply Filter'}
                    </button>
                  </div>
                </div>
                
                {/* Export Buttons Row */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => handleExportReport('pdf')}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </button>
                  <button
                    onClick={() => handleExportReport('excel')}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Excel
                  </button>
                </div>
              </div>
            </div>

            {/* Inventory Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Total Products */}
              <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Products</p>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-600">{inventoryData.totalProducts}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Total Value */}
              <div className="bg-gradient-to-br from-white via-green-50/30 to-emerald-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-green-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Value</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-600">{formatCurrency(inventoryData.totalValue)}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Low Stock Items */}
              <div className="bg-gradient-to-br from-white via-orange-50/30 to-amber-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-orange-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Low Stock Items</p>
                    <p className="text-2xl sm:text-3xl font-bold text-orange-600">{inventoryData.lowStockItems.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Out of Stock */}
              <div className="bg-gradient-to-br from-white via-red-50/30 to-rose-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-red-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                    <p className="text-2xl sm:text-3xl font-bold text-red-600">{inventoryData.outOfStockItems.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-rose-600 rounded-lg flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Alerts - Compact Design */}
            {inventoryData.stockAlerts.length > 0 && (
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-lg p-4 mb-6 sm:mb-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Stock Alerts</h3>
                      <p className="text-xs text-gray-600">{inventoryData.stockAlerts.length} items need attention</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                      {inventoryData.stockAlerts.filter(alert => alert.type === 'out_of_stock').length} Out of Stock
                    </span>
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                      {inventoryData.stockAlerts.filter(alert => alert.type === 'low_stock').length} Low Stock
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Charts and Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
              {/* Supplier Breakdown */}
              <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <PieChart className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Supplier Breakdown</h3>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {inventoryData.supplierBreakdown.map((supplier, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                          <Users className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{supplier.name}</p>
                          <p className="text-xs text-gray-500">Supplier</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-blue-600">{supplier.count} items</p>
                        <p className="text-xs text-gray-500">{formatCurrency(supplier.value)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Moving Products */}
              <div className="bg-gradient-to-br from-white via-green-50/30 to-emerald-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-green-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Top Moving Products</h3>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {inventoryData.topMovingProducts.map((product, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                          <Package className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{product.name}</p>
                          <p className="text-xs text-gray-500">SKU: {product.sku}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-600">{product.quantitySold}</p>
                        <p className="text-xs text-gray-500">units sold</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Low Stock and Out of Stock Items */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
              {/* Low Stock Items */}
              <div className="bg-gradient-to-br from-white via-orange-50/30 to-amber-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-orange-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg flex items-center justify-center mr-3">
                    <AlertTriangle className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Low Stock Items</h3>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {inventoryData.lowStockItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                          <Package className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item.name}</p>
                          <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-orange-600">{item.currentStock}</p>
                        <p className="text-xs text-gray-500">min: {item.minStock}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Out of Stock Items */}
              <div className="bg-gradient-to-br from-white via-red-50/30 to-rose-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-red-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-rose-600 rounded-lg flex items-center justify-center mr-3">
                    <XCircle className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Out of Stock Items</h3>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {inventoryData.outOfStockItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-rose-600 rounded-lg flex items-center justify-center">
                          <Package className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item.name}</p>
                          <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-red-600">0</p>
                        <p className="text-xs text-gray-500">out of stock</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

      </div>
    </Layout>
  );
};

export default InventoryReport;
